import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
  ArrowLeft,
  Activity,
  Heart,
  Zap,
  Moon,
  Timer,
  Settings,
  Smartphone,
  Wind,
  Brain,
  Scale,
  Activity as PulseIcon,
  Footprints,
  AlertCircle,
  RefreshCw
} from 'lucide-react'
import { SectionCard } from '@/components/shared/section-card'
import { StatusBadge } from '@/components/shared/status-badge'
import { HealthChart } from '@/components/charts/health-chart'
import { DataTable } from '@/components/shared/data-table'
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as ReTooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { formatDateTime, formatRelativeTime } from '@/lib/format'
import { 
  useUserDetail, 
  useUserSubcollection,
  useUserHealthData,
} from '@/hooks/useUserDetail'
import { cn } from '@/lib/utils'
import { useAppSettings } from '@/hooks/useAppSettings'
import { DEFAULT_PARLENT_WATCH_MODELS, formatWatchLabel, isParlentWatch } from '@/lib/watch-models'

type TabType = 'overview' | 'heart' | 'sleep' | 'steps' | 'workout' | 'spo2' | 'stress' | 'bp' | 'weight' | 'device'

// Empty State Component
function EmptyState({ 
  title, 
  description, 
  icon: Icon 
}: { 
  title: string
  description: string
  icon: any
}) {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-4 rounded-2xl bg-stone-900/30 border border-stone-800">
      <div className="w-16 h-16 rounded-full bg-stone-800/50 flex items-center justify-center mb-4">
        <Icon className="w-8 h-8 text-stone-600" />
      </div>
      <h3 className="text-lg font-semibold text-stone-300 mb-2">{title}</h3>
      <p className="text-sm text-stone-500 text-center max-w-sm">{description}</p>
    </div>
  )
}

export function UserDetailPage() {
  const { uid } = useParams<{ uid: string }>()

  const [activeTab, setActiveTab] = useState<TabType>('overview')
  
  // Handle undefined UID
  if (!uid || uid === 'undefined') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <div className="w-20 h-20 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mb-6">
          <AlertCircle className="w-10 h-10 text-amber-500" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">Invalid User ID</h2>
        <p className="text-stone-400 mb-6 text-center max-w-md">
          The user ID is missing or invalid. Please select a valid user from the list.
        </p>
        <Link 
          to="/users" 
          className="flex items-center gap-2 px-6 py-3 rounded-xl bg-orange-500 text-white font-medium hover:bg-orange-600 transition"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Users
        </Link>
      </div>
    )
  }
  
  const { user, loading: userLoading, error: userError } = useUserDetail(uid)
  const { data: devices } = useUserSubcollection<any>(uid, 'devices')
  const { settings } = useAppSettings()
  const parlentWatchModels = settings.watchModels?.parlent ?? DEFAULT_PARLENT_WATCH_MODELS
  
  // Use combined health data hook
  const healthData = useUserHealthData(uid)
  
  const { 
    heartRate, 
    steps, 
    sleep, 
    workouts, 
    healthSummary,
    spo2,
    stress,
    bloodPressure,
    weight
  } = healthData

  if (userError) {
    return (
      <EmptyState 
        title="Error Loading User"
        description={`Failed to load user data: ${userError.message}`}
        icon={AlertCircle}
      />
    )
  }

  if (userLoading) {
    return (
      <div className="flex h-[400px] items-center justify-center">
        <div className="flex items-center gap-3">
          <RefreshCw className="h-6 w-6 text-orange-500 animate-spin" />
          <p className="text-stone-400">Loading user profile...</p>
        </div>
      </div>
    )
  }

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <div className="w-20 h-20 rounded-full bg-rose-500/10 border border-rose-500/20 flex items-center justify-center mb-6">
          <AlertCircle className="w-10 h-10 text-rose-500" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">User Not Found</h2>
        <p className="text-stone-400 mb-6">The requested user could not be found.</p>
        <Link 
          to="/users" 
          className="flex items-center gap-2 px-6 py-3 rounded-xl bg-orange-500 text-white font-medium hover:bg-orange-600 transition"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Users
        </Link>
      </div>
    )
  }

  const tabs: { id: TabType; label: string; icon: any; badge?: number }[] = [
    { id: 'overview', label: 'Overview', icon: Activity },
    { id: 'heart', label: 'Heart Rate', icon: Heart, badge: heartRate.data?.length || 0 },
    { id: 'sleep', label: 'Sleep', icon: Moon, badge: sleep.data?.length || 0 },
    { id: 'steps', label: 'Steps', icon: Footprints, badge: steps.data?.length || 0 },
    { id: 'workout', label: 'Workouts', icon: Timer, badge: workouts.data?.length || 0 },
    { id: 'spo2', label: 'SpO2 (Blood Oxygen)', icon: Wind, badge: spo2.data?.length || 0 },
    { id: 'stress', label: 'Stress Level', icon: Brain, badge: stress.data?.length || 0 },
    { id: 'bp', label: 'Blood Pressure', icon: PulseIcon, badge: bloodPressure.data?.length || 0 },
    { id: 'weight', label: 'Weight Tracking', icon: Scale, badge: weight.data?.length || 0 },
    { id: 'device', label: 'Device & Sync', icon: Smartphone },
  ]

  // Calculate sleep averages
  const sleepStats = sleep.data?.length > 0 ? {
    avgDeep: Math.round(sleep.data.reduce((acc, s) => acc + (s.deepMinutes || s.deep_minutes || 0), 0) / sleep.data.length),
    avgLight: Math.round(sleep.data.reduce((acc, s) => acc + (s.lightMinutes || s.light_minutes || 0), 0) / sleep.data.length),
    avgRem: Math.round(sleep.data.reduce((acc, s) => acc + (s.remMinutes || s.rem_minutes || 0), 0) / sleep.data.length),
  } : null

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link 
          to="/users" 
          className="flex h-10 w-10 items-center justify-center rounded-full border border-stone-700 bg-stone-800 text-white shadow-sm transition hover:bg-stone-700"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-white">{user?.displayName || 'User Detail'}</h1>
          <p className="text-sm text-stone-400">{user?.email || user?.uid}</p>
        </div>
        <div className="ml-auto flex gap-2">
          <StatusBadge value={user?.status || 'active'} />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-stone-800 pb-1">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              'flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition relative',
              activeTab === tab.id
                ? 'border-b-2 border-orange-500 text-orange-400'
                : 'text-stone-400 hover:text-stone-200'
            )}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
            {tab.badge && tab.badge > 0 ? (
              <span className="ml-1 rounded-full bg-orange-500/20 text-orange-400 px-2 py-0.5 text-xs font-bold">
                {tab.badge}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Health Snapshot */}
          <SectionCard title="Health Snapshot" description="Daily aggregation from health subcollections">
             <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                {[
                  { label: 'Step Goal', value: `${user?.dailyStepGoal || 0} steps`, icon: Zap, color: 'text-amber-400', bgColor: 'bg-amber-500/10' },
                  { label: 'Sleep Goal', value: `${user?.dailySleepGoal || 0} hrs`, icon: Moon, color: 'text-indigo-400', bgColor: 'bg-indigo-500/10' },
                  { label: 'Last Sync', value: formatRelativeTime(user?.lastSync), icon: Activity, color: 'text-emerald-400', bgColor: 'bg-emerald-500/10' },
                  { label: 'Gender', value: user?.gender || '-', icon: Settings, color: 'text-stone-400', bgColor: 'bg-stone-500/10' },
                ].map((stat, i) => (
                  <div key={i} className="rounded-2xl border border-stone-800 bg-stone-900/50 p-4 hover:bg-stone-800/50 transition">
                    <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center mb-3", stat.bgColor)}>
                      <stat.icon className={cn("h-5 w-5", stat.color)} />
                    </div>
                    <p className="text-sm text-stone-400">{stat.label}</p>
                    <p className="text-xl font-bold text-white mt-1">{stat.value}</p>
                  </div>
                ))}
             </div>
          </SectionCard>

          {/* Charts */}
          <div className="grid gap-6 md:grid-cols-2">
             <SectionCard title="Steps Trend (30 Days)" description="Daily step count">
                {healthSummary.data?.length > 0 ? (
                  <HealthChart 
                    data={[...healthSummary.data].reverse()} 
                    dataKey="steps" 
                    xKey="date" 
                    color="#f59e0b" 
                    label="Steps"
                  />
                ) : (
                  <EmptyState 
                    title="No Steps Data"
                    description="User hasn't synced any step data yet."
                    icon={Footprints}
                  />
                )}
             </SectionCard>
             <SectionCard title="Heart Rate Trend" description="Average daily HR data">
                {healthSummary.data?.length > 0 ? (
                  <HealthChart 
                    data={[...healthSummary.data].reverse()} 
                    dataKey="avgHr" 
                    xKey="date" 
                    color="#ef4444" 
                    label="Avg HR"
                  />
                ) : (
                  <EmptyState 
                    title="No Heart Rate Data"
                    description="User hasn't synced any heart rate data yet."
                    icon={Heart}
                  />
                )}
             </SectionCard>
          </div>

          {/* Additional Sensors Preview */}
          <SectionCard title="Additional Metrics" description="Quick overview from other sensors">
            <div className="grid gap-4 md:grid-cols-4">
              {/* SpO2 Card */}
              <div className="rounded-2xl border border-stone-800 bg-stone-900/50 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-sky-500/10 flex items-center justify-center">
                    <Wind className="h-4 w-4 text-sky-400" />
                  </div>
                  <span className="text-sm text-stone-400">SpO2</span>
                </div>
                <p className="text-2xl font-bold text-white">
                  {spo2.stats?.latest ? `${spo2.stats.latest}%` : '--'}
                </p>
                <p className="text-xs text-stone-500 mt-1">
                  Avg: {spo2.stats?.avg > 0 ? `${spo2.stats.avg}%` : '--'}
                </p>
              </div>

              {/* Stress Card */}
              <div className="rounded-2xl border border-stone-800 bg-stone-900/50 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center">
                    <Brain className="h-4 w-4 text-purple-400" />
                  </div>
                  <span className="text-sm text-stone-400">Stress</span>
                </div>
                <p className="text-2xl font-bold text-white">
                  {stress.stats?.latest !== null ? stress.stats.latest : '--'}
                </p>
                {stress.stats?.level && (
                  <span className={cn(
                    "text-xs px-2 py-0.5 rounded-full font-medium",
                    stress.stats.level === 'low' ? 'bg-emerald-500/20 text-emerald-400' :
                    stress.stats.level === 'moderate' ? 'bg-amber-500/20 text-amber-400' :
                    'bg-rose-500/20 text-rose-400'
                  )}>
                    {stress.stats.level}
                  </span>
                )}
              </div>

              {/* BP Card */}
              <div className="rounded-2xl border border-stone-800 bg-stone-900/50 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-rose-500/10 flex items-center justify-center">
                    <PulseIcon className="h-4 w-4 text-rose-400" />
                  </div>
                  <span className="text-sm text-stone-400">Blood Pressure</span>
                </div>
                <p className="text-2xl font-bold text-white">
                  {bloodPressure.stats?.systolic?.latest && bloodPressure.stats?.diastolic?.latest
                    ? `${bloodPressure.stats.systolic.latest}/${bloodPressure.stats.diastolic.latest}`
                    : '--/--'}
                </p>
                {bloodPressure.stats?.classification && (
                  <p className="text-xs text-stone-500 mt-1 truncate">
                    {bloodPressure.stats.classification}
                  </p>
                )}
              </div>

              {/* Weight Card */}
              <div className="rounded-2xl border border-stone-800 bg-stone-900/50 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-teal-500/10 flex items-center justify-center">
                    <Scale className="h-4 w-4 text-teal-400" />
                  </div>
                  <span className="text-sm text-stone-400">Weight</span>
                </div>
                <p className="text-2xl font-bold text-white">
                  {weight.stats?.latest ? `${weight.stats.latest.toFixed(1)} kg` : '--'}
                </p>
                {weight.stats?.change !== null && weight.stats?.change !== 0 && (
                  <p className={cn(
                    "text-xs mt-1 font-medium",
                    weight.stats.change < 0 ? 'text-emerald-400' : 'text-rose-400'
                  )}>
                    {weight.stats.change > 0 ? '+' : ''}{weight.stats.change.toFixed(1)} kg
                  </p>
                )}
              </div>
            </div>
          </SectionCard>
        </div>
      )}

      {/* Heart Rate Tab */}
      {activeTab === 'heart' && (
        <div className="space-y-6">
          <SectionCard title="Heart Rate History" description="Granular heart rate data from smartwatch sensor">
            {heartRate.data?.length > 0 ? (
              <>
                <HealthChart 
                  data={[...heartRate.data].slice(0, 100).reverse().map(d => ({ 
                    ...d, 
                    time: d.timestamp?.seconds ? new Date(d.timestamp.seconds * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-' 
                  }))}
                  dataKey="bpm"
                  xKey="time"
                  color="#ef4444"
                  label="BPM"
                  yDomain={[40, 200]}
                />
                <div className="mt-6">
                  <DataTable
                    rows={heartRate.data}
                    pageSize={10}
                    columns={[
                      { header: 'BPM', render: (row) => <span className="font-bold text-rose-400">{row.bpm}</span> },
                      { header: 'Type', render: (row) => row.type || 'Automated' },
                      { header: 'Timestamp', render: (row) => formatDateTime(row.timestamp?.seconds ? new Date(row.timestamp.seconds * 1000).toISOString() : null) },
                    ]}
                    emptyTitle="No data"
                    emptyDescription="User has no heart rate data yet."
                  />
                </div>
              </>
            ) : (
              <EmptyState 
                title="No Heart Rate Data"
                description="User hasn't synced any heart rate measurements yet. Data will appear here once the user syncs their smartwatch."
                icon={Heart}
              />
            )}
          </SectionCard>
        </div>
      )}

      {/* Sleep Tab */}
      {activeTab === 'sleep' && (
        <div className="space-y-6">
           <SectionCard title="Sleep Duration Trend" description="Sleep data per night (in minutes)">
              {sleep.data?.length > 0 ? (
                <HealthChart 
                  data={[...sleep.data].reverse()}
                  dataKey="totalDuration"
                  xKey="date"
                  color="#6366f1"
                  label="Duration (min)"
                />
              ) : (
                <EmptyState 
                  title="No Sleep Data"
                  description="User hasn't synced any sleep data yet."
                  icon={Moon}
                />
              )}
           </SectionCard>
           <div className="grid gap-6 md:grid-cols-3">
              <SectionCard title="Deep Sleep" description="Average deep sleep">
                  <p className="text-3xl font-bold text-white">
                    {sleepStats ? `${sleepStats.avgDeep} min` : '-- min'}
                  </p>
              </SectionCard>
               <SectionCard title="Light Sleep" description="Average light sleep">
                  <p className="text-3xl font-bold text-white">
                    {sleepStats ? `${sleepStats.avgLight} min` : '-- min'}
                  </p>
              </SectionCard>
               <SectionCard title="Rem Sleep" description="Average rem sleep">
                  <p className="text-3xl font-bold text-white">
                    {sleepStats ? `${sleepStats.avgRem} min` : '-- min'}
                  </p>
              </SectionCard>
           </div>
        </div>
      )}

      {/* Steps Tab - FIX: Show empty state if no data */}
      {activeTab === 'steps' && (
        <div className="space-y-6">
          <SectionCard title="Steps History" description="Daily step count data">
            {steps.data?.length > 0 ? (
              <>
                <HealthChart 
                  data={[...steps.data].reverse()}
                  dataKey="steps"
                  xKey="date"
                  color="#f59e0b"
                  label="Steps"
                />
                <div className="mt-6">
                  <DataTable
                    rows={steps.data}
                    pageSize={10}
                    columns={[
                      { header: 'Date', render: (row) => row.date },
                      { header: 'Steps', render: (row) => <span className="font-bold text-amber-400">{row.steps?.toLocaleString() || 0}</span> },
                      { header: 'Distance', render: (row) => `${row.distance?.toFixed(2) || 0} km` },
                      { header: 'Calories', render: (row) => `${row.calories || 0} kcal` },
                    ]}
                    emptyTitle="No steps data"
                    emptyDescription="User has no step data yet."
                  />
                </div>
              </>
            ) : (
              <EmptyState 
                title="No Steps Data"
                description="User hasn't synced any step data yet. Steps will be recorded when the user syncs their smartwatch or mobile app."
                icon={Footprints}
              />
            )}
          </SectionCard>
        </div>
      )}

      {/* Workout Tab */}
      {activeTab === 'workout' && (
        <SectionCard title="Workout History" description="Synced exercise activity list">
          {workouts.data?.length > 0 ? (
            <DataTable
              rows={workouts.data}
              pageSize={10}
              columns={[
                { header: 'Activity', render: (row) => <span className="font-bold text-white">{row.sportType || row.type || 'Workout'}</span> },
                { header: 'Duration', render: (row) => `${Math.floor((row.duration || row.durationSeconds || 0)/60)}m ${(row.duration || row.durationSeconds || 0) % 60}s` },
                { header: 'Calories', render: (row) => `${row.calories || 0} kcal` },
                { header: 'Distance', render: (row) => `${(row.distance || row.distanceMeters || 0).toFixed(2)} km` },
                { header: 'Strava', render: (row) => row.stravaUploaded ? <StatusBadge value="Sync" /> : <span className="text-stone-500">-</span> },
                { header: 'Date', render: (row) => formatDateTime(row.startTime) },
              ]}
              emptyTitle="No workouts"
              emptyDescription="User hasn't synced any workouts yet."
            />
          ) : (
            <EmptyState 
              title="No Workouts"
              description="User hasn't recorded any workouts yet. Workouts will appear here once the user completes and syncs an exercise session."
              icon={Timer}
            />
          )}
        </SectionCard>
      )}

      {/* Device Tab */}
      {activeTab === 'device' && (
        <div className="grid gap-6 md:grid-cols-2">
           <SectionCard title="Paired Devices" description="Semua jam yang pernah dipakai user ini.">
              {devices?.length > 0 ? (
                <DataTable 
                  rows={devices}
                  columns={[
                    { header: 'Device', render: (row) => (
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-stone-800 flex items-center justify-center">
                          <Smartphone className="w-5 h-5 text-stone-400" />
                        </div>
                        <div>
                          <p className="flex items-center gap-2 font-bold text-white">
                            {formatWatchLabel(row.name)}
                            {isParlentWatch(row.name, parlentWatchModels) && (
                              <span className="rounded-md border border-orange-500/40 bg-orange-500/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-orange-300">
                                Parlent
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-stone-500 font-mono">{row.address || row.id || 'N/A'}</p>
                        </div>
                      </div>
                    )},
                    { header: 'Model', render: (row) => (
                      <div className="text-sm">
                        <p className="text-stone-300">{row.model || <span className="italic text-stone-600">Belum terkirim</span>}</p>
                        <p className="text-xs text-stone-500">{row.firmwareVersion ? `FW ${row.firmwareVersion}` : '—'}</p>
                      </div>
                    )},
                    { header: 'Battery', render: (row) => (
                      <div className="flex items-center gap-2">
                        <div className={cn(
                          "w-2 h-2 rounded-full",
                          (row.battery || 0) > 20 ? 'bg-emerald-400' : 'bg-rose-400'
                        )} />
                        <span className="text-white">{row.battery || 0}%</span>
                      </div>
                    )},
                    { header: 'Dipakai Sejak', render: (row) => (
                      <div className="text-sm">
                        <p className="text-stone-300">{row.firstConnectedAt ? formatDateTime(row.firstConnectedAt) : '—'}</p>
                        <p className="text-xs text-stone-500">{row.connectionCount ? `${row.connectionCount}x koneksi` : ''}</p>
                      </div>
                    )},
                    { header: 'Last Connected', render: (row) => formatDateTime(row.lastConnected) },
                  ]}
                  emptyTitle="No devices"
                  emptyDescription="User has not paired any device yet."
                />
              ) : (
                <EmptyState 
                  title="No Devices"
                  description="User hasn't paired any smartwatch or device yet. Devices will appear here once paired."
                  icon={Smartphone}
                />
              )}
           </SectionCard>
           <SectionCard title="Sync Metadata" description="Last SQLite to Firestore sync status">
              <div className="space-y-4 p-2">
                 <div className="flex justify-between items-center border-b border-stone-800 pb-3">
                    <span className="text-stone-400">Sync Version</span>
                    <span className="font-medium text-white">{user?.syncVersion || '1.0.0'}</span>
                 </div>
                  <div className="flex justify-between items-center border-b border-stone-800 pb-3">
                    <span className="text-stone-400">Last Successful Sync</span>
                    <span className="font-medium text-white">{formatDateTime(user?.lastSync) || 'Never'}</span>
                 </div>
                 <div className="flex justify-between items-center border-b border-stone-800 pb-3">
                    <span className="text-stone-400">Total Records Synced</span>
                    <span className="font-medium text-white">
                      {(heartRate.data?.length || 0) + (steps.data?.length || 0) + (sleep.data?.length || 0) + (workouts.data?.length || 0)} records
                    </span>
                 </div>
                 <div className="flex justify-between items-center pt-1">
                    <span className="text-stone-400">Data Sources</span>
                    <span className="font-medium text-stone-300">
                      {[
                        heartRate.data?.length > 0 && 'HR', 
                        steps.data?.length > 0 && 'Steps', 
                        sleep.data?.length > 0 && 'Sleep', 
                        workouts.data?.length > 0 && 'Workout'
                      ].filter(Boolean).join(', ') || 'None'}
                    </span>
                 </div>
              </div>
           </SectionCard>
        </div>
      )}

      {/* SpO2 Tab */}
      {activeTab === 'spo2' && (
        <div className="space-y-6">
           <SectionCard title="SpO2 (Blood Oxygen)" description="Blood oxygen saturation">
              {spo2.data?.length > 0 ? (
                <>
                  <div className="grid gap-4 md:grid-cols-4 mb-6">
                    <div className="rounded-2xl bg-sky-500/10 border border-sky-500/20 p-4">
                      <p className="text-sm text-sky-400">Latest</p>
                      <p className="text-2xl font-bold text-white">{spo2.stats?.latest ? `${spo2.stats.latest}%` : '--'}</p>
                    </div>
                    <div className="rounded-2xl bg-stone-800 border border-stone-700 p-4">
                      <p className="text-sm text-stone-400">Average</p>
                      <p className="text-2xl font-bold text-white">{spo2.stats?.avg > 0 ? `${spo2.stats.avg}%` : '--'}</p>
                    </div>
                    <div className="rounded-2xl bg-stone-800 border border-stone-700 p-4">
                      <p className="text-sm text-stone-400">Min</p>
                      <p className="text-2xl font-bold text-white">{spo2.stats?.min > 0 ? `${spo2.stats.min}%` : '--'}</p>
                    </div>
                    <div className="rounded-2xl bg-stone-800 border border-stone-700 p-4">
                      <p className="text-sm text-stone-400">Max</p>
                      <p className="text-2xl font-bold text-white">{spo2.stats?.max > 0 ? `${spo2.stats.max}%` : '--'}</p>
                    </div>
                  </div>
                  <div className="mb-6">
                    <HealthChart
                      data={[...spo2.data].reverse().map(d => ({
                        ...d,
                        time: d.timestamp?.seconds
                          ? new Date(d.timestamp.seconds * 1000).toLocaleString([], { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
                          : '-',
                      }))}
                      dataKey="value"
                      xKey="time"
                      color="#0ea5e9"
                      label="SpO2 %"
                      yDomain={[80, 100]}
                    />
                  </div>
                  <DataTable
                    rows={spo2.data}
                    pageSize={10}
                    columns={[
                      { header: 'SpO2 %', render: (row) => <span className="font-bold text-sky-400">{row.value || row.spo2}%</span> },
                      { header: 'Timestamp', render: (row) => formatDateTime(row.timestamp?.seconds ? new Date(row.timestamp.seconds * 1000).toISOString() : null) },
                    ]}
                    emptyTitle="No SpO2 data"
                    emptyDescription="User has no blood oxygen data."
                  />
                </>
              ) : (
                <EmptyState
                  title="No SpO2 Data"
                  description="User hasn't synced any blood oxygen measurements yet."
                  icon={Wind}
                />
              )}
           </SectionCard>
        </div>
      )}

      {/* Stress Tab */}
      {activeTab === 'stress' && (
        <div className="space-y-6">
           <SectionCard title="Stress Level" description="Daily stress level readings">
              {stress.data?.length > 0 ? (
                <>
                  <div className="grid gap-4 md:grid-cols-3 mb-6">
                    <div className={cn(
                      "rounded-2xl p-4 border",
                      stress.stats?.level === 'low' ? 'bg-emerald-500/10 border-emerald-500/20' :
                      stress.stats?.level === 'moderate' ? 'bg-amber-500/10 border-amber-500/20' :
                      stress.stats?.level === 'high' ? 'bg-rose-500/10 border-rose-500/20' :
                      'bg-stone-800 border-stone-700'
                    )}>
                      <p className={cn(
                        "text-sm",
                        stress.stats?.level === 'low' ? 'text-emerald-400' :
                        stress.stats?.level === 'moderate' ? 'text-amber-400' :
                        stress.stats?.level === 'high' ? 'text-rose-400' :
                        'text-stone-500'
                      )}>Current Level</p>
                      <p className="text-2xl font-bold text-white">{stress.stats?.latest !== null ? stress.stats.latest : '--'}</p>
                      {stress.stats?.level && (
                        <span className={cn(
                          "text-xs px-2 py-0.5 rounded-full font-medium",
                          stress.stats.level === 'low' ? 'bg-emerald-500/20 text-emerald-400' :
                          stress.stats.level === 'moderate' ? 'bg-amber-500/20 text-amber-400' :
                          'bg-rose-500/20 text-rose-400'
                        )}>
                          {stress.stats.level}
                        </span>
                      )}
                    </div>
                    <div className="rounded-2xl bg-stone-800 border border-stone-700 p-4">
                      <p className="text-sm text-stone-400">Average</p>
                      <p className="text-2xl font-bold text-white">{stress.stats?.avg > 0 ? stress.stats.avg : '--'}</p>
                    </div>
                    <div className="rounded-2xl bg-stone-800 border border-stone-700 p-4">
                      <p className="text-sm text-stone-400">Records</p>
                      <p className="text-2xl font-bold text-white">{stress.data?.length || 0}</p>
                    </div>
                  </div>
                  <div className="mb-6">
                    <HealthChart
                      data={[...stress.data].reverse().map(d => ({
                        ...d,
                        time: d.timestamp?.seconds
                          ? new Date(d.timestamp.seconds * 1000).toLocaleString([], { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
                          : '-',
                      }))}
                      dataKey="level"
                      xKey="time"
                      color="#a855f7"
                      label="Stress Level"
                      yDomain={[0, 100]}
                    />
                  </div>
                  <DataTable
                    rows={stress.data}
                    pageSize={10}
                    columns={[
                      { header: 'Level', render: (row) => (
                        <span className={cn(
                          "font-bold",
                          (row.level || row.stress) <= 30 ? 'text-emerald-400' :
                          (row.level || row.stress) <= 60 ? 'text-amber-400' :
                          'text-rose-400'
                        )}>
                          {row.level || row.stress}
                        </span>
                      )},
                      { header: 'Timestamp', render: (row) => formatDateTime(row.timestamp?.seconds ? new Date(row.timestamp.seconds * 1000).toISOString() : null) },
                    ]}
                    emptyTitle="No stress data"
                    emptyDescription="User has no stress level data."
                  />
                </>
              ) : (
                <EmptyState
                  title="No Stress Data"
                  description="User hasn't synced any stress level measurements yet."
                  icon={Brain}
                />
              )}
           </SectionCard>
        </div>
      )}

      {/* Blood Pressure Tab */}
      {activeTab === 'bp' && (
        <div className="space-y-6">
           <SectionCard title="Blood Pressure" description="Systolic and diastolic blood pressure">
              {bloodPressure.data?.length > 0 ? (
                <>
                  <div className="grid gap-4 md:grid-cols-3 mb-6">
                    <div className="rounded-2xl bg-rose-500/10 border border-rose-500/20 p-4">
                      <p className="text-sm text-rose-400">Latest Reading</p>
                      <p className="text-2xl font-bold text-white">
                        {bloodPressure.stats?.systolic?.latest && bloodPressure.stats?.diastolic?.latest
                          ? `${bloodPressure.stats.systolic.latest}/${bloodPressure.stats.diastolic.latest}`
                          : '--/--'}
                      </p>
                      <span className="text-xs text-rose-400/70">mmHg</span>
                    </div>
                    <div className="rounded-2xl bg-stone-800 border border-stone-700 p-4">
                      <p className="text-sm text-stone-400">Average</p>
                      <p className="text-2xl font-bold text-white">
                        {bloodPressure.stats?.systolic?.avg > 0 && bloodPressure.stats?.diastolic?.avg > 0
                          ? `${bloodPressure.stats.systolic.avg}/${bloodPressure.stats.diastolic.avg}`
                          : '--/--'}
                      </p>
                      <span className="text-xs text-stone-500">mmHg</span>
                    </div>
                    <div className={cn(
                      "rounded-2xl p-4 border",
                      bloodPressure.stats?.classification === 'Normal' ? 'bg-emerald-500/10 border-emerald-500/20' :
                      bloodPressure.stats?.classification === 'Elevated' ? 'bg-amber-500/10 border-amber-500/20' :
                      'bg-rose-500/10 border-rose-500/20'
                    )}>
                      <p className={cn(
                        "text-sm",
                        bloodPressure.stats?.classification === 'Normal' ? 'text-emerald-400' :
                        bloodPressure.stats?.classification === 'Elevated' ? 'text-amber-400' :
                        'text-rose-400'
                      )}>Classification</p>
                      <p className="text-lg font-bold text-white">
                        {bloodPressure.stats?.classification || '--'}
                      </p>
                    </div>
                  </div>
                  <div className="h-[300px] w-full mb-6">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={[...bloodPressure.data].reverse().map(d => ({
                          ...d,
                          time: d.timestamp?.seconds
                            ? new Date(d.timestamp.seconds * 1000).toLocaleString([], { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
                            : '-',
                        }))}
                        margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#44403c" />
                        <XAxis
                          dataKey="time"
                          axisLine={false}
                          tickLine={false}
                          tick={{ fill: '#a8a29e', fontSize: 11 }}
                          minTickGap={30}
                        />
                        <YAxis
                          axisLine={false}
                          tickLine={false}
                          tick={{ fill: '#a8a29e', fontSize: 11 }}
                          domain={[40, 200]}
                        />
                        <ReTooltip
                          contentStyle={{
                            backgroundColor: '#1c1917',
                            borderRadius: '12px',
                            border: '1px solid #44403c',
                            color: '#fafaf9',
                          }}
                          labelStyle={{ fontWeight: 'bold', marginBottom: '4px', color: '#fafaf9' }}
                        />
                        <Legend wrapperStyle={{ fontSize: 12, color: '#a8a29e' }} />
                        <Line type="monotone" dataKey="systolic" stroke="#f43f5e" strokeWidth={2} dot={false} name="Systolic" />
                        <Line type="monotone" dataKey="diastolic" stroke="#fb7185" strokeWidth={2} dot={false} name="Diastolic" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  <DataTable
                    rows={bloodPressure.data}
                    pageSize={10}
                    columns={[
                      { header: 'Systolic', render: (row) => <span className="font-bold text-rose-400">{row.systolic}</span> },
                      { header: 'Diastolic', render: (row) => <span className="font-bold text-rose-400">{row.diastolic}</span> },
                      { header: 'Timestamp', render: (row) => formatDateTime(row.timestamp?.seconds ? new Date(row.timestamp.seconds * 1000).toISOString() : null) },
                    ]}
                    emptyTitle="No BP data"
                    emptyDescription="User has no blood pressure data."
                  />
                </>
              ) : (
                <EmptyState
                  title="No Blood Pressure Data"
                  description="User hasn't synced any blood pressure measurements yet."
                  icon={PulseIcon}
                />
              )}
           </SectionCard>
        </div>
      )}

      {/* Weight Tab */}
      {activeTab === 'weight' && (
        <div className="space-y-6">
           <SectionCard title="Weight Tracking" description="User weight history">
              {weight.data?.length > 0 ? (
                <>
                  <div className="grid gap-4 md:grid-cols-4 mb-6">
                    <div className="rounded-2xl bg-teal-500/10 border border-teal-500/20 p-4">
                      <p className="text-sm text-teal-400">Current</p>
                      <p className="text-2xl font-bold text-white">
                        {weight.stats?.latest ? `${weight.stats.latest.toFixed(1)} kg` : '--'}
                      </p>
                    </div>
                    <div className="rounded-2xl bg-stone-800 border border-stone-700 p-4">
                      <p className="text-sm text-stone-400">Min</p>
                      <p className="text-2xl font-bold text-white">
                        {weight.stats?.min ? `${weight.stats.min.toFixed(1)} kg` : '--'}
                      </p>
                    </div>
                    <div className="rounded-2xl bg-stone-800 border border-stone-700 p-4">
                      <p className="text-sm text-stone-400">Max</p>
                      <p className="text-2xl font-bold text-white">
                        {weight.stats?.max ? `${weight.stats.max.toFixed(1)} kg` : '--'}
                      </p>
                    </div>
                    <div className={cn(
                      "rounded-2xl p-4 border",
                      weight.stats?.change && weight.stats.change < 0 ? 'bg-emerald-500/10 border-emerald-500/20' :
                      weight.stats?.change && weight.stats.change > 0 ? 'bg-rose-500/10 border-rose-500/20' :
                      'bg-stone-800 border-stone-700'
                    )}>
                      <p className={cn(
                        "text-sm",
                        weight.stats?.change && weight.stats.change < 0 ? 'text-emerald-400' :
                        weight.stats?.change && weight.stats.change > 0 ? 'text-rose-400' :
                        'text-stone-500'
                      )}>Change</p>
                      <p className={cn(
                        "text-2xl font-bold",
                        weight.stats?.change && weight.stats.change < 0 ? 'text-emerald-400' :
                        weight.stats?.change && weight.stats.change > 0 ? 'text-rose-400' :
                        'text-white'
                      )}>
                        {weight.stats?.change !== null ? `${weight.stats.change > 0 ? '+' : ''}${weight.stats.change.toFixed(1)} kg` : '--'}
                      </p>
                    </div>
                  </div>
                  <div className="mb-6">
                    <HealthChart
                      data={[...weight.data].reverse().map(d => ({
                        ...d,
                        time: d.date || (d.timestamp?.seconds
                          ? new Date(d.timestamp.seconds * 1000).toLocaleDateString([], { month: '2-digit', day: '2-digit' })
                          : '-'),
                      }))}
                      dataKey="weightKg"
                      xKey="time"
                      color="#14b8a6"
                      label="Weight (kg)"
                    />
                  </div>
                  <DataTable
                    rows={weight.data}
                    pageSize={10}
                    columns={[
                      { header: 'Weight', render: (row) => (
                        <span className="font-bold text-teal-400">
                          {(row.weightKg || row.weight || 0).toFixed(1)} kg
                        </span>
                      )},
                      { header: 'Date', render: (row) => row.date || formatDateTime(row.timestamp?.seconds ? new Date(row.timestamp.seconds * 1000).toISOString() : null) },
                      { header: 'Note', render: (row) => <span className="text-stone-400">{row.note || '-'}</span> },
                    ]}
                    emptyTitle="No weight data"
                    emptyDescription="User has no weight data."
                  />
                </>
              ) : (
                <EmptyState 
                  title="No Weight Data"
                  description="User hasn't recorded any weight measurements yet."
                  icon={Scale}
                />
              )}
           </SectionCard>
        </div>
      )}
    </div>
  )
}
